import { useTranslation } from 'react-i18next';
import {
  Button,
  GoogleFontIcon,
  TextWithTooltip,
} from '@asset-metrix/portal-ui';
import { Badge, Group, Stack, Text } from '@mantine/core';
import clsx from 'clsx';

import type { DashboardModalRow } from './utils/dashboard.types';

import styles from './dashboard-modals.module.scss';

export type AllDashboardsRowAction =
  'moveUp' | 'moveDown' | 'hide' | 'show' | 'delete' | 'restore';

export type AllDashboardsModalRowProps = {
  row: DashboardModalRow;
  variant: 'instanced' | 'available';
  instanceIndex?: number;
  instanceCount?: number;
  isOwner: boolean;
  pendingAction?: AllDashboardsRowAction | null;
  actionsDisabled?: boolean;
  /** When true, Show tab is hidden and Delete is replaced by Restore. */
  markedForDeletion?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onHide?: () => void;
  onShow?: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
};

export function AllDashboardsModalRow({
  row,
  variant,
  instanceIndex = 0,
  instanceCount = 0,
  isOwner,
  pendingAction,
  actionsDisabled,
  markedForDeletion = false,
  onMoveUp,
  onMoveDown,
  onHide,
  onShow,
  onDelete,
  onRestore,
}: AllDashboardsModalRowProps) {
  const { t } = useTranslation();

  const sharingLabel =
    row.sharingStatus === 'global'
      ? t('app.dashboard.allDashboardsModal.sharingGlobal')
      : t('app.dashboard.allDashboardsModal.sharingPrivate');

  const rowBusy = actionsDisabled === true;
  const trimmedDescription = row.description?.trim();
  const hasDescription =
    trimmedDescription != null && trimmedDescription.length > 0;

  const availableActions = [
    {
      title: 'app.dashboard.allDashboardsModal.showTab',
      icon: <GoogleFontIcon name="visibility" small />,
      loadingState: 'show',
      onClick: onShow,
      isVisible: !markedForDeletion,
    },
    {
      title: 'app.dashboard.allDashboardsModal.restore',
      icon: <GoogleFontIcon name="undo" small />,
      loadingState: 'restore',
      onClick: onRestore,
      isVisible: isOwner && markedForDeletion,
      'data-testid': `dashboard-modal-row-restore-${row.id}`,
    },
    {
      title: 'app.dashboard.allDashboardsModal.delete',
      icon: <GoogleFontIcon name="delete" small />,
      loadingState: 'delete',
      onClick: onDelete,
      isVisible: isOwner && !markedForDeletion,
    },
  ] as const;

  const actions =
    variant === 'available'
      ? availableActions.filter((action) => action.isVisible)
      : [];

  return (
    <Group
      align="center"
      className={styles.row}
      data-testid={`dashboard-modal-row-${row.id}`}
      gap="lg"
      justify="space-between"
      wrap="wrap"
    >
      <Stack className={styles.rowMain} gap={4}>
        <Group gap="sm" wrap="wrap">
          <TextWithTooltip
            textProps={{
              fw: 600,
            }}
            tooltipProps={{ usePortal: false }}
          >
            {row.name}
          </TextWithTooltip>
          <Badge
            classNames={{
              root: clsx(styles.badge, {
                [styles['badge--global']]: row.sharingStatus === 'global',
                [styles['badge--private']]: row.sharingStatus === 'private',
              }),
            }}
            unstyled
          >
            {sharingLabel}
          </Badge>
        </Group>
        {hasDescription && (
          <Text className={styles.rowDescription}>{trimmedDescription}</Text>
        )}
      </Stack>
      <Group
        className={styles.rowActions}
        gap="xs"
        justify="flex-end"
        wrap="wrap"
      >
        {variant === 'instanced' && (
          <>
            <Group gap={2} wrap="nowrap">
              {instanceIndex > 0 && (
                <Button
                  disabled={rowBusy}
                  icon={<GoogleFontIcon name="arrow_upward" small />}
                  loading={pendingAction === 'moveUp'}
                  title={t('app.dashboard.allDashboardsModal.moveUp')}
                  variant="secondary"
                  onClick={onMoveUp}
                />
              )}
              {instanceIndex < instanceCount - 1 && (
                <Button
                  disabled={rowBusy}
                  icon={<GoogleFontIcon name="arrow_downward" small />}
                  loading={pendingAction === 'moveDown'}
                  title={t('app.dashboard.allDashboardsModal.moveDown')}
                  variant="secondary"
                  onClick={onMoveDown}
                />
              )}
            </Group>
            <Button
              disabled={rowBusy}
              icon={<GoogleFontIcon name="visibility_off" small />}
              loading={pendingAction === 'hide'}
              title={t('app.dashboard.allDashboardsModal.hideTab')}
              variant="secondary"
              onClick={onHide}
            >
              {t('app.dashboard.allDashboardsModal.hideTab')}
            </Button>
          </>
        )}
        {actions.map(
          ({ title, loadingState, isVisible: _isVisible, ...action }) => (
            <Button
              key={title}
              variant="secondary"
              disabled={rowBusy}
              loading={pendingAction === loadingState}
              title={t(title)}
              {...action}
            >
              {t(title)}
            </Button>
          )
        )}
      </Group>
    </Group>
  );
}
